import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Send, CheckCircle2, Clock, AlertTriangle, Sparkles } from "lucide-react";

const KIND_LABELS = {
  lead_thank_you: "Applicant thank-you",
  lead_magnet: "Guide delivery",
  enrollment_welcome: "Enrollment welcome",
  application_alert: "New application alert",
  nurture_1: "Nurture 1 · The #1 mistake",
  nurture_2: "Nurture 2 · Client story",
  nurture_3: "Nurture 3 · Book a call",
};

const STATUS_STYLE = {
  sent: { icon: CheckCircle2, color: "var(--accent)" },
  queued: { icon: Clock, color: "var(--text-tertiary)" },
  failed: { icon: AlertTriangle, color: "#B4553C" },
};

export default function CoachEmails() {
  const [data, setData] = useState({ resend_configured: false, emails: [] });
  const [enrollments, setEnrollments] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/emails").then((r) => setData(r.data)).catch(() => {});
    api.get("/auto-enrollments").then((r) => setEnrollments(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const flush = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/emails/flush");
      toast.success(`Sent ${res.sent} emails${res.failed ? `, ${res.failed} failed` : ""}`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const queued = data.emails.filter((e) => e.status === "queued").length;

  return (
    <div className="space-y-10" data-testid="coach-emails">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Automations</div>
          <h1 className="font-serif text-5xl mt-3">Emails</h1>
          <p className="text-secondary-luxe text-sm mt-3 max-w-2xl">
            Automatic thank-you notes to applicants, guide deliveries, and enrollment welcomes.
          </p>
        </div>
        {queued > 0 && data.resend_configured && (
          <button className="btn-primary" onClick={flush} disabled={busy} data-testid="flush-emails-btn">
            <Send size={13} /> {busy ? "Sending…" : `Send ${queued} queued`}
          </button>
        )}
      </header>

      {!data.resend_configured && (
        <div className="card-luxe" data-testid="resend-not-configured">
          <div className="overline flex items-center gap-2"><Sparkles size={12} /> Activation pending</div>
          <p className="text-sm text-secondary-luxe mt-3">
            Emails are being written and saved below, but not sent yet — add your Resend API key
            to activate delivery. Everything queued here will go out the moment it's connected.
          </p>
        </div>
      )}

      {data.scheduled > 0 && (
        <div className="card-luxe" data-testid="nurture-scheduled">
          <div className="overline flex items-center gap-2"><Clock size={12} /> Nurture sequence</div>
          <p className="text-sm text-secondary-luxe mt-3">
            {data.scheduled} nurture email{data.scheduled === 1 ? "" : "s"} scheduled — new guide subscribers
            automatically receive a 3-email series (day 1, 3 and 5) warming them toward a discovery call.
          </p>
        </div>
      )}

      {enrollments.length > 0 && (
        <div>
          <div className="overline mb-3">Auto-enrollments (via Stripe)</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {enrollments.map((e) => (
              <div key={e.auto_enrollment_id} className="card-luxe" data-testid={`enrollment-${e.auto_enrollment_id}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-serif text-xl">{e.name}</h3>
                  <span className="text-xs" style={{ color: "var(--accent)" }}>${e.amount}</span>
                </div>
                <div className="text-xs text-secondary-luxe mt-1">{e.email}</div>
                <div className="text-xs text-tertiary-luxe mt-2">{e.created_at?.slice(0, 10)} · {e.invite_sent ? "Invite emailed" : "Existing member enrolled"}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3">
        <div className="overline">Outbox</div>
        {data.emails.map((e) => {
          const s = STATUS_STYLE[e.status] || STATUS_STYLE.queued;
          const Icon = s.icon;
          return (
            <div key={e.email_id} className="card-luxe flex items-center justify-between gap-4 flex-wrap" data-testid={`email-${e.email_id}`}>
              <div className="flex items-center gap-4 min-w-0">
                <Mail size={16} className="text-secondary-luxe flex-shrink-0" />
                <div className="min-w-0">
                  <div className="font-serif text-lg truncate">{e.subject}</div>
                  <div className="text-xs text-secondary-luxe mt-0.5">
                    To {e.to} · {KIND_LABELS[e.kind] || e.kind} · {e.created_at?.slice(0, 16).replace("T", " ")}
                  </div>
                  {e.error && <div className="text-xs mt-1" style={{ color: "#B4553C" }}>{e.error}</div>}
                </div>
              </div>
              <span className="flex items-center gap-1.5 font-label text-[10px] tracking-[0.2em] uppercase" style={{ color: s.color }}>
                <Icon size={13} /> {e.status}
              </span>
            </div>
          );
        })}
        {data.emails.length === 0 && <div className="text-secondary-luxe text-sm">No emails yet — they'll appear here as applications and payments come in.</div>}
      </div>
    </div>
  );
}
